const CONSTRUCTOR_CONFIG = {
  RESULTS_PER_PAGE: 40,
  CURRENT_PAGE: 1,
  MAX_RETRIES: 3,
  BASE_DELAY_MS: 1000,
  MAX_DELAY_MS: 10000,
  TIMEOUT: 5000,
  FIRST_BATCH_SIZE: 8,
  CHUNK_SIZE: 10,
};

const CONSTRUCTOR_SELECTORS = {
  resultsContainer: "#constructor-browse-results",
  browseBanner: "[data-cnstrc-browse]",
  filtersBody: "#constructor-filters-body",
  filtersOffcanvas: "#constructor-offcanvas-filters",
  sortDropdown: "#sort-dropdown",
  currentSort: "#current-sort",
  loadMoreContainer: ".constructor-load-more",
  loadMoreBtn: ".constructor-load-more-btn",
  resultsMessage: ".collection-results-message",
  progressBar: "#collection-pagination .progress-bar",
  fallbackTemplate: "#shopify-fallback-template",
  fallbackPagination: ".constructor-fallback-pagination",
  priceInputMin: '.constructor-price-input[data-filter-name="price_min"]',
  priceInputMax: '.constructor-price-input[data-filter-name="price_max"]',
};

/**
 * @typedef {Object} ConstructorProduct
 * @property {string} handle - Product handle/slug
 * @property {Object} constructorData - Raw Constructor.io data
 * @property {Array} variations - Product variations
 * @property {boolean} isSlotted - Whether product is slotted
 */

/**
 * @class
 */
class ConstructorBrowse {
  constructor() {
    this.client = null;
    this.requestSequence = 0;
    this.retryCount = 0;
    this.currentCollectionHandle = "";
    this.currentFilters = {};
    this.currentFacets = [];
    this.currentSortOptions = [];
    this.collectionBanners = [];
    this.totalResultsFromAPI = 0;
    this._domCache = new Map();
    this._currentAbort = null;
    this._cardObserver = null;
    this.currentDisplayPage = 1; // Track the current page being displayed for Load More
    this.hasLoadedMore = false; // True after user clicks Load More at least once
    this._infiniteScrollObserver = null; // IntersectionObserver for infinite scroll
    this._isInfiniteLoading = false; // Guard against concurrent infinite scroll fetches
    this._scrollHandler = null; // Scroll handler for infinite scroll
    this.isInitialized = true; // Mark as initialized

    this.loadCollectionBanners();
    this.bindEvents();

    document.dispatchEvent(
      new CustomEvent("constructor:browse:ready", {
        detail: { instance: this },
      }),
    );
  }

  /**
   * Fetch products from a collection
   * @param {string} collectionHandle - Collection handle
   * @param {Object} options - Search options
   * @returns {Promise<boolean>} - Success of operation
   */
  async handleBrowse(collectionHandle, options = {}) {
    if (!this.client) {
      this.client = window.constructorio;
    }

    if (!this.client) {
      return false;
    }

    if (this._currentAbort) {
      this._currentAbort.abort();
    }
    this._currentAbort = new AbortController();

    this.requestSequence++;
    const mySequence = this.requestSequence;

    this.currentCollectionHandle = collectionHandle;

    if (options.filters !== undefined) {
      this.currentFilters = options.filters;
    }

    const urlParams = new URLSearchParams(window.location.search);

    if (options.filters === undefined) {
      const urlFilters = urlParams.get("filters");
      if (urlFilters) {
        try {
          const parsedFilters = JSON.parse(decodeURIComponent(urlFilters));
          this.currentFilters = { ...parsedFilters, ...this.currentFilters };
        } catch (e) {}
      }
    }

    const defaults = {
      page: CONSTRUCTOR_CONFIG.CURRENT_PAGE,
      resultsPerPage: CONSTRUCTOR_CONFIG.RESULTS_PER_PAGE,
      sortBy: "relevance",
      sortOrder: "descending",
      section: "Products",
    };

    const urlOverrides = {
      page: parseInt(urlParams.get("page")) || undefined,
      resultsPerPage: parseInt(urlParams.get("resultsPerPage")) || undefined,
      sortBy: urlParams.get("sortBy") || undefined,
      sortOrder: urlParams.get("sortOrder") || undefined,
    };

    const legacySort = urlParams.get("sort");
    if (legacySort && legacySort.includes(":")) {
      const [sb, so] = legacySort.split(":");
      if (!urlOverrides.sortBy) urlOverrides.sortBy = sb;
      if (!urlOverrides.sortOrder) urlOverrides.sortOrder = so;
    }

    const browseParams = {
      ...defaults,
      ...Object.fromEntries(Object.entries(urlOverrides).filter(([_, v]) => v !== undefined)),
      ...Object.fromEntries(Object.entries(options).filter(([_, v]) => v !== undefined && v !== null && v !== "")),
    };

    if (this.currentFilters && Object.keys(this.currentFilters).length > 0) {
      browseParams.filters = this.currentFilters;
    }

    try {
      const response = await this.client.browse.getBrowseResults("group_id", collectionHandle, browseParams, {
        timeout: CONSTRUCTOR_CONFIG.TIMEOUT,
        signal: this._currentAbort.signal,
      });

      if (mySequence !== this.requestSequence) {
        return false;
      }

      const results = response.response?.results || response.results || [];

      this.currentFacets = response.response?.facets || [];
      this.currentSortOptions = response.response?.sort_options || [];

      const priceFacet = this.currentFacets.find((f) => f.type === "range" && f.name === "price");
      if (priceFacet) {
        this.priceLimits = {
          min: priceFacet.min || 0,
          max: priceFacet.max || 999999,
        };
      }

      this.updateSortDropdown();
      this.loadConstructorFilters();

      if (options.appendToExisting && results.length === 0) {
        this.hideLoadMore();
        return true;
      }

      const constructorProducts = results
        .map((item) => {
          const handle =
            item.data?.url?.split("/products/")[1]?.split("?")[0] ||
            item.data?.handle ||
            item.value?.toLowerCase().replace(/\s+/g, "-");

          return {
            handle,
            ...item.data,
            variations: item.variations ?? [],
            value: item.value,
            matchedTerms: item.matched_terms ?? [],
            isSlotted: item.is_slotted ?? false,
            constructorData: item,
          };
        })
        .filter((item) => item.handle);

      const productHandles = constructorProducts.map((item) => item.handle);

      this.totalResultsFromAPI = response.response?.total_num_results || results.length;

      const currentlyDisplayed = await this.renderProducts(productHandles, {
        collectionHandle: collectionHandle,
        resultId: response.result_id,
        totalResults: this.totalResultsFromAPI,
        constructorProducts: constructorProducts,
        currentPage: browseParams.page,
        resultsPerPage: browseParams.resultsPerPage,
        ...options,
      });

      // Only update URL if not appending (i.e., fresh page load or filter change)
      if (!options.appendToExisting) {
        this.updateURL(collectionHandle, {
          page: browseParams.page,
          resultsPerPage: browseParams.resultsPerPage,
          sortBy: browseParams.sortBy,
          sortOrder: browseParams.sortOrder,
          filters: this.currentFilters,
        });
      }

      // Use requestAnimationFrame to ensure DOM is updated before checking if we should hide the button
      requestAnimationFrame(() => {
        this.renderLoadMore({
          currentPage: browseParams.page,
          resultsPerPage: browseParams.resultsPerPage,
          totalResults: this.totalResultsFromAPI,
          collectionHandle: collectionHandle,
          currentlyDisplayed: currentlyDisplayed,
        });
      });

      return true;
    } catch (error) {
      if (error.name === "AbortError") {
        return false;
      }

      if (this._shouldRetry(error)) {
        const retryDelay = this._calculateRetryDelay();
        this.retryCount++;

        if (this.retryCount <= CONSTRUCTOR_CONFIG.MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return this.handleBrowse(collectionHandle, options);
        }
      }

      this.retryCount = 0;

      this.showErrorState();

      return false;
    }
  }

  /**
   * Create a DocumentFragment from HTML string for batch DOM operations
   * @param {string} htmlString - HTML string to convert
   * @returns {DocumentFragment} Fragment ready to insert
   */
  _createProductFragment(htmlString) {
    const template = document.createElement("template");
    template.innerHTML = htmlString.trim();
    return template.content;
  }

  /**
   * Defer heavy component initialization to avoid blocking LCP
   * @param {Element} container - Container element to initialize
   */
  _deferComponentInitialization(container) {
    const idleCallback = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));

    idleCallback(
      () => {
        this.reinitializeComponents(container);
      },
      { timeout: 100 },
    );
  }

  /**
   * Optimize LCP image with priority hints and preload
   * @param {Element} grid - Product grid element
   */
  _optimizeLCPImage(grid) {
    if (!grid) return;

    const firstImg = grid.querySelector(".product-item-img--primary, .product-item-img");
    if (!firstImg) return;

    if (!firstImg.getAttribute("width")) firstImg.setAttribute("width", "600");
    if (!firstImg.getAttribute("height")) firstImg.setAttribute("height", "600");

    firstImg.setAttribute("loading", "eager");
    firstImg.setAttribute("fetchpriority", "high");
    firstImg.setAttribute("decoding", "async");

    if (!document.querySelector('link[rel="preload"][as="image"][data-cnstrc-lcp]')) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = firstImg.currentSrc || firstImg.src;
      link.setAttribute("data-cnstrc-lcp", "");
      document.head.appendChild(link);
    }

    grid.querySelectorAll("img:not(:first-of-type)").forEach((img) => {
      if (!img.hasAttribute("loading")) img.setAttribute("loading", "lazy");
      if (!img.hasAttribute("decoding")) img.setAttribute("decoding", "async");
      if (!img.hasAttribute("fetchpriority")) img.setAttribute("fetchpriority", "low");
    });
  }

  /**
   * Transform products asynchronously in chunks to avoid blocking main thread
   * @param {Array} constructorProducts - Products from Constructor.io API
   * @returns {Promise<Array>} Transformed products
   */
  async _transformProductsAsync(constructorProducts) {
    const results = [];

    for (let i = 0; i < constructorProducts.length; i += CONSTRUCTOR_CONFIG.CHUNK_SIZE) {
      const chunk = constructorProducts.slice(i, i + CONSTRUCTOR_CONFIG.CHUNK_SIZE);

      const transformed = chunk.map((product) => this._transformSingleProduct(product));
      results.push(...transformed);

      if (i + CONSTRUCTOR_CONFIG.CHUNK_SIZE < constructorProducts.length) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }
    }

    return results;
  }

  /**
   * Transform a single Constructor.io product into internal format
   * @param {Object} constructorProduct - Constructor.io product
   * @returns {Object} Transformed product
   */
  _transformSingleProduct(constructorProduct) {
    const data = constructorProduct.constructorData?.data || {};
    const variations = constructorProduct.variations || [];

    return {
      handle: constructorProduct.handle,
      title: constructorProduct.value || data.title || "Product",
      url: data.url || `/products/${constructorProduct.handle}`,
      available: this._isVariantAvailable(data),
      id: data.id,
      price: data.price || 0,
      compare_at_price: data.compareAtPrice || 0,
      featured_image: {
        alt: constructorProduct.value || data.title || "Product",
        src: data.image_url || "",
      },
      images: data.mediaImages || [],
      vendor: data.vendor || "",
      product_type: data.product_type || "",
      has_only_default_variant: variations.length <= 1,
      options: (() => {
        const optionsMap = new Map();
        let hasExplicitOptions = false;

        variations.forEach((variation) => {
          const varData = variation.data || {};

          // Check if we have explicit color/size fields
          if (varData.color) {
            hasExplicitOptions = true;
            if (!optionsMap.has("Color")) {
              optionsMap.set("Color", { name: "Color", values: new Set() });
            }
            optionsMap.get("Color").values.add(varData.color);
          }
          if (varData.size) {
            hasExplicitOptions = true;
            if (!optionsMap.has("Size")) {
              optionsMap.set("Size", { name: "Size", values: new Set() });
            }
            optionsMap.get("Size").values.add(varData.size);
          }
        });

        // Fallback: Parse title field if no explicit options found
        if (!hasExplicitOptions && variations.length > 0) {
          variations.forEach((variation) => {
            const varData = variation.data || {};
            const title = varData.title || "";

            if (title) {
              if (title.includes(" / ")) {
                const parts = title.split(" / ").map((part) => part.trim());

                if (parts[0]) {
                  if (!optionsMap.has("Option1")) {
                    optionsMap.set("Option1", { name: "Option1", values: new Set() });
                  }
                  optionsMap.get("Option1").values.add(parts[0]);
                }

                if (parts[1]) {
                  if (!optionsMap.has("Option2")) {
                    optionsMap.set("Option2", { name: "Option2", values: new Set() });
                  }
                  optionsMap.get("Option2").values.add(parts[1]);
                }
              } else {
                // Single-option format: "Silver Waterproof", "18K Gold Waterproof"
                if (!optionsMap.has("Option1")) {
                  optionsMap.set("Option1", { name: "Option1", values: new Set() });
                }
                optionsMap.get("Option1").values.add(title.trim());
              }
            }
          });
        }

        return Array.from(optionsMap.entries()).map(([name, option]) => ({
          name,
          values: Array.from(option.values),
        }));
      })(),
      variants: variations?.map((v) => this._transformVariant(v, data)) || [],
      constructorData: constructorProduct.constructorData,
      matchedTerms: constructorProduct.matchedTerms || [],
      isSlotted: constructorProduct.isSlotted || false,
      tags: [
        ...(data.group_ids || []),
        ...(data.tags || []),
        data.preorder_tag && "preorder",
        data.waterproof_tag && "waterproof",
        data.bestseller_tag && "bestseller",
        data.new_tag && "new",
      ].filter(Boolean),
    };
  }

  async renderProducts(handles, options = {}) {
    if (!handles || handles.length === 0) {
      this.showNoResults(options.collectionHandle);
      return;
    }

    try {
      const constructorProducts = options.constructorProducts || [];

      if (constructorProducts.length === 0) {
        this.showNoResults(options.collectionHandle);
        return;
      }

      const combinedProducts = await this._transformProductsAsync(constructorProducts);
      const { firstHTML, restHTML } = this.renderProductsTemplate(combinedProducts, options);
      const resultsContainer = this.getResultsContainer();

      if (!resultsContainer) {
        this.showErrorState();
        return;
      }

      const existingGrid = resultsContainer.querySelector("[data-cnstrc-browse]");
      let finalShowingCount;

      if (existingGrid) {
        if (options.appendToExisting) {
          const allHTML = firstHTML + restHTML;
          const fragment = this._createProductFragment(allHTML);
          existingGrid.appendChild(fragment);
          const currentCount = parseInt(existingGrid.getAttribute("data-cnstrc-num-results")) || 0;
          const newTotalCount = currentCount + combinedProducts.length;
          // Cap at total available to prevent showing more than exists
          const cappedCount = Math.min(newTotalCount, this.totalResultsFromAPI);
          existingGrid.setAttribute("data-cnstrc-num-results", cappedCount);
          finalShowingCount = cappedCount;
        } else {
          const firstFragment = this._createProductFragment(firstHTML);
          existingGrid.classList.add("constructor-ready");
          existingGrid.replaceChildren(firstFragment);
          existingGrid.setAttribute("data-cnstrc-num-results", combinedProducts.length);

          if (restHTML) {
            const appendRest = () => {
              const restFragment = this._createProductFragment(restHTML);
              existingGrid.appendChild(restFragment);
            };
            const scheduleAppend = window.requestIdleCallback || window.requestAnimationFrame;
            scheduleAppend(appendRest, { timeout: 200 });
          }

          const currentPage = options.currentPage || 1;
          const resultsPerPage = options.resultsPerPage || CONSTRUCTOR_CONFIG.RESULTS_PER_PAGE;
          const totalResults = options.totalResults || this.totalResultsFromAPI || combinedProducts.length;
          finalShowingCount = Math.min(currentPage * resultsPerPage, totalResults);
        }
        existingGrid.style.display = "";
        existingGrid.classList.add("constructor-loaded");
      } else {
        const gridHTML = `
          <ul class="product-list list-unstyled row mx-n3 mx-lg-n4 mb-0 constructor-loaded constructor-ready" 
              role="list"
              aria-label="Products in this collection"
              data-cnstrc-browse 
              data-cnstrc-collection-handle="${options.collectionHandle || ""}"
              data-cnstrc-num-results="${combinedProducts.length}"
              data-cnstrc-result-id="${options.resultId || ""}">
            ${firstHTML}
          </ul>
        `;
        resultsContainer.innerHTML = gridHTML;

        if (restHTML) {
          const grid = resultsContainer.querySelector("[data-cnstrc-browse]");
          const appendRest = () => {
            const restFragment = this._createProductFragment(restHTML);
            grid.appendChild(restFragment);
          };
          const scheduleAppend = window.requestIdleCallback || window.requestAnimationFrame;
          scheduleAppend(appendRest, { timeout: 200 });
        }

        finalShowingCount = combinedProducts.length;
      }

      this.updateResultsMessage(finalShowingCount);

      const finalGrid = resultsContainer.querySelector("[data-cnstrc-browse]");
      if (finalGrid && options.resultId) {
        finalGrid.setAttribute("data-cnstrc-result-id", options.resultId);
      }

      const fallbackPagination = resultsContainer.querySelector(".constructor-fallback-pagination");
      if (fallbackPagination) {
        fallbackPagination.style.display = "none";
      }

      if (!options.appendToExisting) {
        this._optimizeLCPImage(finalGrid);
      }

      this._deferComponentInitialization(resultsContainer);

      document.dispatchEvent(
        new CustomEvent("constructor:browse:results", {
          detail: {
            numResults: combinedProducts.length,
            collectionHandle: options.collectionHandle,
            appendToExisting: options.appendToExisting || false,
          },
        }),
      );
      return finalShowingCount;
    } catch (error) {
      this.showErrorState();
    }
  }

  renderProductsTemplate(products, options = {}) {
    if (!products || products.length === 0) {
      return { firstHTML: '<li class="col-12"><p class="text-center">No products found.</p></li>', restHTML: "" };
    }

    const productHTMLArray = products.map((product, index) => {
      return this.renderSingleProductTemplate(product, {
        ...options,
        productIndex: index,
      });
    });

    const finalHTMLArray = this.insertBannersIntoProducts(productHTMLArray);

    return {
      firstHTML: finalHTMLArray.slice(0, CONSTRUCTOR_CONFIG.FIRST_BATCH_SIZE).join(""),
      restHTML: finalHTMLArray.slice(CONSTRUCTOR_CONFIG.FIRST_BATCH_SIZE).join(""),
    };
  }

  productHasTag(product, requiredTag) {
    if (!requiredTag) return false;

    const data = product.constructorData?.data || {};

    const tagMap = {
      preorder: data.preorder_tag,
      "pre-order": data.preorder_tag,
      "new-preorder": data.new_preorder_tag,
      new_preorder: data.new_preorder_tag,
      waterproof: data.waterproof_tag,
      bestseller: data.bestseller_tag,
      "best-seller": data.bestseller_tag,
      bestsellers: data.bestseller_tag,
      new: data.new_tag,
      "low-inventory": data.low_inventory_tag,
    };

    if (tagMap[requiredTag] !== undefined) {
      return tagMap[requiredTag] === true;
    }

    if (data.tags) {
      return data.tags.includes(requiredTag);
    }

    if (data.group_ids && Array.isArray(data.group_ids)) {
      return data.group_ids.includes(requiredTag);
    }

    return false;
  }

  /**
   * Transform Constructor.io variation to Shopify variant format
   * @private
   */
  _transformVariant(variant, parentData) {
    const data = variant.data || {};
    const variantTitle = data.title || variant.value || "Variant";

    // Parse title for option values if color/size not provided
    let color = data.color;
    let size = data.size;
    let option1, option2;

    if (!color && !size && variantTitle) {
      if (variantTitle.includes(" / ")) {
        const parts = variantTitle.split(" / ").map((part) => part.trim());
        option1 = parts[0] || undefined;
        option2 = parts[1] || undefined;
      } else {
        option1 = variantTitle.trim();
      }
    }

    return {
      id: data.variation_id || variant.id,
      title: variantTitle,
      price: data.price || parentData.price || 0,
      compare_at_price: data.compareAtPrice || parentData.compareAtPrice || 0,
      inventory_quantity: data.inventory_quantity || 0,
      available: this._isVariantAvailable(data),
      image_url: data.image_url,
      color: color,
      size: size,
      option1: option1,
      option2: option2,
      discountPercentage: data.discountPercentage || data.fake_discount_percentage || 0,
      featured_image: {
        src: data.image_url || parentData.image_url || "",
      },
      inventory_policy: (data.inventoryPolicy || data.inventory_policy || "deny").toLowerCase(),
    };
  }

  renderSingleProductTemplate(product, options = {}) {
    if (!product) return "";

    const config = ConstructorUtils.getCachedThemeConfig();

    return ConstructorUtils.renderProductCard(product, {
      ...options,
      config,
      renderAddToCartDesktop: (prod) => this.getSmartAddToCartForm(prod),
      renderAddToCartMobile: (prod) => this.getSmartAddToCartFormMobile(prod),
    });
  }

  getResultsContainer() {
    let container = document.querySelector("#constructor-browse-results");
    if (container) {
      return container;
    }

    container = document.querySelector("[data-cnstrc-browse]");
    if (container && container.parentElement) {
      return container.parentElement;
    }

    container = document.querySelector("#collection .container");
    if (container) {
      return container;
    }

    return null;
  }

  updateResultsMessage(totalResults) {
    const messageElement = document.querySelector(".collection-results-message");
    if (messageElement) {
      const totalAvailable = this.totalResultsFromAPI || totalResults;

      messageElement.textContent = `Showing ${totalResults} of ${totalAvailable} products`;
      messageElement.classList.remove("constructor-hide-until-ready");
      messageElement.style.display = "";

      this.updateProgressBar(totalResults, totalAvailable);
    }
  }

  updateProgressBar(currentResults, totalResults) {
    const progressBar = document.querySelector("#collection-pagination .progress-bar");
    if (progressBar && totalResults > 0) {
      const percentage = Math.round((currentResults / totalResults) * 100);
      progressBar.style.width = `${percentage}%`;
      progressBar.setAttribute("aria-valuenow", currentResults);
      progressBar.setAttribute("aria-valuemax", totalResults);
    }
  }

  showErrorState() {
    const container = this.getResultsContainer();
    if (container) {
      const tpl = document.getElementById("shopify-fallback-template");
      if (tpl) {
        const ul = document.createElement("ul");
        ul.className = "product-list list-unstyled row mb-0";
        ul.setAttribute("role", "list");
        ul.setAttribute("aria-label", "Products in this collection");

        const clonedContent = tpl.content.cloneNode(true);
        ul.appendChild(clonedContent);

        const existingGrid = container.querySelector("[data-cnstrc-browse]");
        if (existingGrid) {
          existingGrid.replaceWith(ul);
        } else {
          container.innerHTML = "";
          container.appendChild(ul);
        }
      } else {
        const errorEl = document.createElement("div");
        errorEl.className = "alert alert-warning py-3";
        errorEl.setAttribute("role", "status");
        errorEl.textContent = "Search temporarily unavailable. Please try again.";

        const existingGrid = container.querySelector("[data-cnstrc-browse]");
        if (existingGrid) {
          existingGrid.classList.add("constructor-loaded");
          existingGrid.replaceWith(errorEl);
        }
      }
    }

    this.hideLoadMore();
  }

  /**
   * Get cached DOM element
   * @private
   */
  _getCachedElement(selector, useGetElementById = false) {
    if (!this._domCache.has(selector)) {
      const element = useGetElementById ? document.getElementById(selector) : document.querySelector(selector);
      this._domCache.set(selector, element);
    }
    return this._domCache.get(selector);
  }

  /**
   * Calculate retry delay with exponential backoff
   * @private
   */
  _calculateRetryDelay() {
    return Math.min(CONSTRUCTOR_CONFIG.BASE_DELAY_MS * Math.pow(2, this.retryCount), CONSTRUCTOR_CONFIG.MAX_DELAY_MS);
  }

  /**
   * Check if error is retryable
   * @private
   */
  _shouldRetry(error) {
    return error.name === "NetworkError" || error.code === "ECONNABORTED";
  }

  /**
   * Check if variant/product is available based on inventory policy and quantity
   * @private
   * @param {Object} data - Variant or product data
   * @returns {boolean} True if available
   */
  _isVariantAvailable(data) {
    if (!data) return false;

    const policy = (data.inventoryPolicy || data.inventory_policy || "DENY").toUpperCase();
    const quantity = data.inventory_quantity || 0;

    return policy === "CONTINUE" || quantity > 0;
  }

  /**
   * Check URL parameters and perform browse if needed (for page loads)
   */
  checkUrlAndBrowse() {
    const isCollectionPage = window.location.pathname.includes("/collections/");

    if (isCollectionPage) {
      const pathParts = window.location.pathname.split("/");
      const collectionIndex = pathParts.indexOf("collections");
      const collectionHandle = pathParts[collectionIndex + 1];

      // Allow "all" collection now - removed the !== "all" check
      if (collectionHandle) {
        const urlParams = new URLSearchParams(window.location.search);
        const page = parseInt(urlParams.get("page")) || CONSTRUCTOR_CONFIG.CURRENT_PAGE;
        const resultsPerPage = parseInt(urlParams.get("resultsPerPage")) || CONSTRUCTOR_CONFIG.RESULTS_PER_PAGE;

        this.handleBrowse(collectionHandle, {
          page: page,
          resultsPerPage: resultsPerPage,
        });
      }
    }
  }

  toggleFilters() {
    const offcanvas = this._getCachedElement("constructor-offcanvas-filters", true);

    if (offcanvas) {
      this.loadConstructorFilters();

      const bsOffcanvas = bootstrap.Offcanvas.getOrCreateInstance(offcanvas);
      bsOffcanvas.show();
    }
  }

  async loadConstructorFilters() {
    const filtersBody = document.getElementById("constructor-filters-body");

    if (!filtersBody) {
      return;
    }

    if (this.currentFacets && this.currentFacets.length > 0) {
      const filtersHTML = ConstructorUtils.generateFiltersFromFacets(this.currentFacets, this.currentFilters);
      filtersBody.innerHTML = filtersHTML;
      this.bindFilterEvents();
    } else if (window.Shopify && window.Shopify.designMode) {
      const hardcodedFacets = this.getHardcodedFacets();
      const filtersHTML = ConstructorUtils.generateFiltersFromFacets(hardcodedFacets, this.currentFilters);
      filtersBody.innerHTML = filtersHTML;
      this.bindFilterEvents();
    } else {
      filtersBody.innerHTML = '<p class="text-muted">No filters available</p>';
    }
  }

  getHardcodedFacets() {
    return [
      {
        name: "vendor",
        display_name: "Brand",
        options: [
          { value: "evry-jewels", display_name: "Evry Jewels", count: 0 },
          {
            value: "gold-collection",
            display_name: "Gold Collection",
            count: 0,
          },
          {
            value: "silver-collection",
            display_name: "Silver Collection",
            count: 0,
          },
        ],
      },
      {
        name: "product_type",
        display_name: "Product Type",
        options: [
          { value: "necklaces", display_name: "Necklaces", count: 0 },
          { value: "earrings", display_name: "Earrings", count: 0 },
          { value: "bracelets", display_name: "Bracelets", count: 0 },
          { value: "rings", display_name: "Rings", count: 0 },
        ],
      },
      {
        name: "tags",
        display_name: "Material",
        options: [
          { value: "gold", display_name: "Gold", count: 0 },
          { value: "silver", display_name: "Silver", count: 0 },
          { value: "rose-gold", display_name: "Rose Gold", count: 0 },
          {
            value: "sterling-silver",
            display_name: "Sterling Silver",
            count: 0,
          },
        ],
      },
    ];
  }

  bindFilterEvents() {
    const debouncedApplyFilters = ConstructorUtils.debounce(() => {
      this.applyFilters();
    }, 300);

    const filtersBody = document.getElementById("constructor-filters-body");
    if (filtersBody) {
      const existingChangeHandler = filtersBody.__constructorChangeHandler;
      const existingInputHandler = filtersBody.__constructorInputHandler;
      const existingBlurHandler = filtersBody.__constructorBlurHandler;
      const existingKeydownHandler = filtersBody.__constructorKeydownHandler;

      if (existingChangeHandler) {
        filtersBody.removeEventListener("change", existingChangeHandler);
      }
      if (existingInputHandler) {
        filtersBody.removeEventListener("input", existingInputHandler);
      }
      if (existingBlurHandler) {
        filtersBody.removeEventListener("blur", existingBlurHandler, true);
      }
      if (existingKeydownHandler) {
        filtersBody.removeEventListener("keydown", existingKeydownHandler);
      }

      const changeHandler = (e) => {
        if (e.target.classList.contains("constructor-filter-checkbox")) {
          this.updateCurrentFilters(e.target);
          debouncedApplyFilters();
        }
      };

      const inputHandler = (e) => {
        if (e.target.classList.contains("constructor-price-input")) {
          this.updatePriceFilters(e.target, false);
          e.target.style.borderColor = "#ffc107";
          e.target.style.boxShadow = "0 0 0 0.2rem rgba(255, 193, 7, 0.25)";
        }
      };

      const blurHandler = (e) => {
        if (e.target.classList.contains("constructor-price-input")) {
          const isSuppressed = !!this.suppressBlurApply;

          this.updatePriceFilters(e.target, true);

          const isMin = e.target.getAttribute("data-filter-name") === "price_min";
          const siblingSelector = isMin
            ? '.constructor-price-input[data-filter-name="price_max"]'
            : '.constructor-price-input[data-filter-name="price_min"]';
          const sibling = document.querySelector(siblingSelector);
          if (sibling) {
            this.updatePriceFilters(sibling, true);
          }

          e.target.style.borderColor = "";
          e.target.style.boxShadow = "";
          if (!isSuppressed) {
            debouncedApplyFilters();
          }
        }
      };

      const keydownHandler = (e) => {
        if (e.target.classList.contains("constructor-price-input") && e.key === "Enter") {
          e.target.blur();
        }
      };

      filtersBody.addEventListener("change", changeHandler);
      filtersBody.addEventListener("input", inputHandler);
      filtersBody.addEventListener("blur", blurHandler, true);
      filtersBody.addEventListener("keydown", keydownHandler);

      filtersBody.__constructorChangeHandler = changeHandler;
      filtersBody.__constructorInputHandler = inputHandler;
      filtersBody.__constructorBlurHandler = blurHandler;
      filtersBody.__constructorKeydownHandler = keydownHandler;
    }
  }

  updatePriceFilters(input, normalize = false) {
    const filterName = input.getAttribute("data-filter-name");
    const inputValue = input.value.trim();
    let value = inputValue === "" ? null : parseInt(inputValue);

    delete this.currentFilters.price;

    if (normalize && value !== null && !isNaN(value) && this.priceLimits) {
      const minLimit = this.priceLimits.min;
      const maxLimit = this.priceLimits.max;

      if (filterName === "price_min") {
        if (value < minLimit) value = minLimit;
        if (value > maxLimit) value = maxLimit;
        input.value = value;

        const currentMax = this.currentFilters.price_max ?? maxLimit;
        if (currentMax !== null && !isNaN(currentMax) && value > currentMax) {
          this.currentFilters.price_max = value;
          const maxInput = document.querySelector('.constructor-price-input[data-filter-name="price_max"]');
          if (maxInput) maxInput.value = value;
        }
      } else if (filterName === "price_max") {
        if (value > maxLimit) value = maxLimit;
        if (value < minLimit) value = minLimit;
        input.value = value;

        const currentMin = this.currentFilters.price_min ?? minLimit;
        if (currentMin !== null && !isNaN(currentMin) && value < currentMin) {
          this.currentFilters.price_min = value;
          const minInput = document.querySelector('.constructor-price-input[data-filter-name="price_min"]');
          if (minInput) minInput.value = value;
        }
      }
    }

    if (filterName === "price_min") {
      if (value === null || isNaN(value)) {
        delete this.currentFilters.price_min;
      } else if (value <= 0) {
        delete this.currentFilters.price_min;
        input.value = "";
      } else {
        this.currentFilters.price_min = value;
      }
    } else if (filterName === "price_max") {
      if (value === null || isNaN(value)) {
        delete this.currentFilters.price_max;
      } else if (value <= 0) {
        delete this.currentFilters.price_max;
        input.value = "";
      } else {
        this.currentFilters.price_max = value;
      }
    }
  }

  updateCurrentFilters(checkbox) {
    const filterName = checkbox.getAttribute("data-filter-name");
    const filterValue = checkbox.getAttribute("data-filter-value");

    if (!this.currentFilters[filterName]) {
      this.currentFilters[filterName] = [];
    }

    if (checkbox.checked) {
      if (!this.currentFilters[filterName].includes(filterValue)) {
        this.currentFilters[filterName].push(filterValue);
      }
    } else {
      this.currentFilters[filterName] = this.currentFilters[filterName].filter((value) => value !== filterValue);

      if (this.currentFilters[filterName].length === 0) {
        delete this.currentFilters[filterName];
      }
    }

    const keysToDelete = [];
    Object.keys(this.currentFilters).forEach((key) => {
      const value = this.currentFilters[key];
      if (!value || !Array.isArray(value) || value.length === 0) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      delete this.currentFilters[key];
    });
  }

  applyFiltersWithPriceInputs() {
    const priceInputs = document.querySelectorAll(".constructor-price-input");
    const minInput = document.querySelector('.constructor-price-input[data-filter-name="price_min"]');
    const maxInput = document.querySelector('.constructor-price-input[data-filter-name="price_max"]');

    if (minInput) this.updatePriceFilters(minInput, true);
    if (maxInput) this.updatePriceFilters(maxInput, true);

    priceInputs.forEach((input) => {
      input.style.borderColor = "";
      input.style.boxShadow = "";
    });

    this.applyFilters();
  }

  applyFilters() {
    const collectionHandle = this.currentCollectionHandle || window.cnstrc?.collectionHandle || "";

    if (!collectionHandle) {
      return;
    }

    const cleanFilters = {};
    Object.keys(this.currentFilters).forEach((key) => {
      const value = this.currentFilters[key];

      if (Array.isArray(value) && value.length > 0) {
        cleanFilters[key] = value;
      } else if ((key === "price_min" || key === "price_max") && value !== "" && value !== null && value !== undefined) {
        cleanFilters[key] = value;
      }
    });

    // Preserve mobile filter tag if it exists
    if (window.constructorMobileFilters && window.constructorMobileFilters.activeFilter) {
      const mobileTag = window.constructorMobileFilters.activeFilter.tag;
      if (mobileTag) {
        if (!cleanFilters.tags) {
          cleanFilters.tags = [];
        }
        if (!cleanFilters.tags.includes(mobileTag)) {
          cleanFilters.tags.push(mobileTag);
        }
      }
    }

    if (cleanFilters.price_min || cleanFilters.price_max) {
      if (cleanFilters.price_min && cleanFilters.price_max) {
        cleanFilters.price = `${cleanFilters.price_min}-${cleanFilters.price_max}`;
      } else if (cleanFilters.price_min && !cleanFilters.price_max) {
        const maxLimit = this.priceLimits ? this.priceLimits.max : 999999;
        cleanFilters.price = `${cleanFilters.price_min}-${maxLimit}`;
      } else if (!cleanFilters.price_min && cleanFilters.price_max) {
        const minLimit = this.priceLimits ? this.priceLimits.min : 0;
        cleanFilters.price = `${minLimit}-${cleanFilters.price_max}`;
      }

      delete cleanFilters.price_min;
      delete cleanFilters.price_max;
    }

    this.currentFilters = cleanFilters;

    const urlParams = new URLSearchParams(window.location.search);
    const hasNoFilters = Object.keys(cleanFilters).length === 0;
    const currentResultsPerPage = parseInt(urlParams.get("resultsPerPage")) || CONSTRUCTOR_CONFIG.RESULTS_PER_PAGE;

    const resultsPerPageToUse = hasNoFilters ? CONSTRUCTOR_CONFIG.RESULTS_PER_PAGE : currentResultsPerPage;

    // Reset pagination tracking when applying filters (going back to page 1)
    this.currentDisplayPage = 1;
    this.hasLoadedMore = false;
    this._isInfiniteLoading = false;
    if (this._infiniteScrollObserver) {
      this._infiniteScrollObserver.disconnect();
      this._infiniteScrollObserver = null;
    }
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    }

    this.handleBrowse(collectionHandle, {
      filters: cleanFilters,
      page: 1,
      resultsPerPage: resultsPerPageToUse,
    });
  }

  showNoResults(collectionHandle) {
    const container = this.getResultsContainer();
    if (container) {
      const noResultsEl = document.createElement("p");
      noResultsEl.className = "alert alert-warning py-3";
      noResultsEl.setAttribute("role", "status");
      noResultsEl.setAttribute("data-cnstrc-zero-result", "");
      noResultsEl.textContent = `No products found in collection "${collectionHandle}"`;

      const existingGrid = container.querySelector("[data-cnstrc-browse]");
      if (existingGrid) {
        existingGrid.classList.add("constructor-loaded");
        existingGrid.replaceWith(noResultsEl);
      }
    }

    this.hideLoadMore();
  }

  reinitializeComponents(container) {
    document.dispatchEvent(
      new CustomEvent("constructor:products-loaded", {
        detail: { container },
      }),
    );

    container.querySelectorAll(".banner-video[data-autoplay='true']").forEach((video) => {
      video.addEventListener(
        "canplay",
        function handleCanPlay() {
          this.play().catch(() => {});
          this.removeEventListener("canplay", handleCanPlay);
        },
        { once: true },
      );
    });

    if (window.ProductCard) {
      if (!this._cardObserver) {
        this._cardObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !entry.target.__pcInit) {
                if (window.ProductCard) {
                  new ProductCard(entry.target);
                }
                entry.target.__pcInit = true;
                this._cardObserver.unobserve(entry.target);
              }
            });
          },
          { rootMargin: "200px" },
        );
      }

      container.querySelectorAll(".product-item").forEach((card) => {
        if (!card.__pcInit) {
          this._cardObserver.observe(card);
        }
      });
    }
  }

  updateURL(collectionHandle, params = {}) {
    const url = new URL(window.location);

    if (params.page && params.page > 1) {
      url.searchParams.set("page", params.page);
    } else {
      url.searchParams.delete("page");
    }

    if (params.resultsPerPage && params.resultsPerPage !== CONSTRUCTOR_CONFIG.RESULTS_PER_PAGE) {
      url.searchParams.set("resultsPerPage", params.resultsPerPage);
    } else {
      url.searchParams.delete("resultsPerPage");
    }

    if (params.sortBy && params.sortBy !== "relevance") {
      url.searchParams.set("sortBy", params.sortBy);
      url.searchParams.set("sort", `${params.sortBy}:${params.sortOrder || "descending"}`);
    } else {
      url.searchParams.delete("sortBy");
      url.searchParams.delete("sort");
    }

    if (params.sortOrder && params.sortOrder !== "descending") {
      url.searchParams.set("sortOrder", params.sortOrder);
    } else {
      url.searchParams.delete("sortOrder");
    }

    const hasActiveFilters =
      params.filters &&
      Object.keys(params.filters).some((key) => {
        const value = params.filters[key];
        return Array.isArray(value) && value.length > 0;
      });

    if (hasActiveFilters) {
      url.searchParams.set("filters", encodeURIComponent(JSON.stringify(params.filters)));
    } else {
      url.searchParams.delete("filters");
    }
    window.history.pushState(null, "", url.toString());
  }

  renderLoadMore(options) {
    const { currentPage, resultsPerPage, totalResults, collectionHandle, currentlyDisplayed } = options;
    
    let loadMoreContainer = document.querySelector(".constructor-load-more");
    if (!loadMoreContainer) {
      const resultsContainer = this.getResultsContainer();
      if (resultsContainer) {
        loadMoreContainer = document.createElement("div");
        loadMoreContainer.className = "constructor-load-more d-flex justify-content-center align-items-center mt-5 mb-4";
        loadMoreContainer.style.cssText = "padding: 20px 0;";
        resultsContainer.appendChild(loadMoreContainer);
      }
    }

    if (!loadMoreContainer) return;

    // Get the actual displayed count from the grid
    const existingGrid = this.getResultsContainer()?.querySelector("[data-cnstrc-browse]");
    const displayedCount = existingGrid ? parseInt(existingGrid.getAttribute("data-cnstrc-num-results")) || 0 : currentlyDisplayed || 0;

    // Check if all products are loaded
    if (displayedCount >= totalResults) {
      loadMoreContainer.style.display = "none";
      return;
    }

    const loadMoreHTML = this.generateLoadMoreHTML(currentPage, collectionHandle, resultsPerPage);
    
    loadMoreContainer.innerHTML = loadMoreHTML;

    this.bindLoadMoreEvents(loadMoreContainer);

    loadMoreContainer.style.display = "flex";
  }

  generateLoadMoreHTML(currentPage, collectionHandle, resultsPerPage) {
    return `
      <button
        class="btn btn-link text-dark text-decoration-none d-inline-flex align-items-center constructor-load-more-btn"
        data-next-page="${this.currentDisplayPage + 1}"
        data-collection-handle="${collectionHandle}"
        data-per-page="${resultsPerPage}"
        data-action="load-more"
        aria-label="Load more products">
        ${this.getLoadMoreButtonContent()}
      </button>
    `;
  }

  getLoadMoreButtonContent(isLoading = false) {
    if (isLoading) {
      return `
        <div class="spinner-border spinner-border-sm me-2" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <span style="font-size: 14px; font-weight: 400;">Loading...</span>
      `;
    }

    return `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="me-2"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
      <span style="font-size: 14px; font-weight: 400;">Load more products</span>
    `;
  }

  setLoadMoreButtonState(isLoading = false, disabled = false) {
    const loadMoreBtn = document.querySelector(".constructor-load-more-btn");
    if (loadMoreBtn) {
      loadMoreBtn.disabled = disabled;
      loadMoreBtn.innerHTML = this.getLoadMoreButtonContent(isLoading);
    }
  }

  bindLoadMoreEvents(container) {
    const loadMoreBtn = container.querySelector(".constructor-load-more-btn");
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", (e) => {
        this._handleLoadMore(e, loadMoreBtn);
      });
    }
  }

  hideLoadMore() {
    if (this._infiniteScrollObserver) {
      this._infiniteScrollObserver.disconnect();
      this._infiniteScrollObserver = null;
    }
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    }
    this._isInfiniteLoading = false;
    const loadMoreContainer = document.querySelector(".constructor-load-more");
    if (loadMoreContainer) {
      loadMoreContainer.remove();
    }
  }

  updateSortDropdown() {
    if (!this.currentSortOptions || this.currentSortOptions.length === 0) {
      return;
    }

    const sortDropdown = document.getElementById("sort-dropdown");
    const currentSortDisplay = document.getElementById("current-sort");

    if (!sortDropdown) {
      return;
    }

    const sortHTML = ConstructorUtils.generateSortOptionsFromData(this.currentSortOptions);
    sortDropdown.innerHTML = sortHTML;

    sortDropdown.querySelectorAll(".dropdown-item").forEach((dropdownItem) => {
      dropdownItem.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        document.querySelectorAll("#sort-dropdown .dropdown-item").forEach((item) => item.classList.remove("selected"));
        dropdownItem.classList.add("selected");

        if (currentSortDisplay) {
          currentSortDisplay.textContent = dropdownItem.textContent;
        }

        sortDropdown.style.display = "none";

        this.handleBrowse(this.currentCollectionHandle, {
          sortBy: dropdownItem.getAttribute("data-value"),
          sortOrder: dropdownItem.getAttribute("data-order"),
          filters: this.currentFilters,
        });
      });
    });

    const selectedOption = this.currentSortOptions.find((opt) => opt.status === "selected");
    if (selectedOption && currentSortDisplay) {
      currentSortDisplay.textContent = selectedOption.display_name;
    }
  }

  async loadMoreProducts(collectionHandle, nextPage, resultsPerPage) {
    const existingGrid = this.getResultsContainer()?.querySelector("[data-cnstrc-browse]");
    const currentlyDisplayed = existingGrid ? parseInt(existingGrid.getAttribute("data-cnstrc-num-results")) || 0 : 0;

    if (currentlyDisplayed >= this.totalResultsFromAPI) {
      this.hideLoadMore();
      return false;
    }

    this.setLoadMoreButtonState(true, true);

    const success = await this.handleBrowse(collectionHandle, {
      page: nextPage,
      resultsPerPage: resultsPerPage,
      appendToExisting: true,
    });

    this.setLoadMoreButtonState(false, false);

    if (success) {
      // Update the current display page
      this.currentDisplayPage = nextPage;
      
      // Check if we've loaded all products and hide button if so
      setTimeout(() => {
        const grid = this.getResultsContainer()?.querySelector("[data-cnstrc-browse]");
        const displayed = grid ? parseInt(grid.getAttribute("data-cnstrc-num-results")) || 0 : 0;
        if (displayed >= this.totalResultsFromAPI) {
          this.hideLoadMore();
        }
      }, 100);
    }

    return success;
  }

  loadCollectionBanners() {
    if (window.constructorCollectionBanners && Array.isArray(window.constructorCollectionBanners)) {
      this.collectionBanners = window.constructorCollectionBanners;
    } else {
      this.collectionBanners = [];
    }
  }

  /**
   * Get appropriate banners for current device type
   * @returns {Array} Array of banner configurations
   */
  getActiveBanners() {
    if (!this.collectionBanners.length) return [];

    const isMobile = window.innerWidth < 576;

    return this.collectionBanners.filter((banner) => {
      return banner.type === (isMobile ? "mobile" : "desktop");
    });
  }

  renderCollectionBanner(banner) {
    try {
      const settings = banner.settings;
      const config = ConstructorUtils.getCachedThemeConfig();
      const marginClass = config.cardStyle !== "card" ? "mb-4" : "";

      const mediaHTML = this._buildBannerMedia(settings, config);
      if (!mediaHTML) return "";

      const overlayHTML = this._buildBannerOverlay(settings);
      const linkWrapper = this._getBannerLinkWrapper(settings.url);

      return `
      <li class="collection-banner-item product-item splide__slide ${config.spacingMobile} ${
        config.spacingDesktop
      } ${marginClass}" 
          role="listitem"
          data-banner-id="${banner.id}"
          data-banner-inject="${banner.inject}">
        <div class="${config.cardStyle || ""}" style="height: 100%;">
          <div class="img-wrapper position-relative h-100">
            ${linkWrapper.start}
              ${mediaHTML}
              ${overlayHTML}
            ${linkWrapper.end}
          </div>
        </div>
      </li>
    `;
    } catch (error) {
      return "";
    }
  }

  /**
   * Build banner media (video or image)
   * @private
   */
  _buildBannerMedia(settings, config) {
    const hasVideo = settings.videoUrl && settings.videoUrl.trim() !== "";
    const hasImage = settings.imgSrc && settings.imgSrc.trim() !== "";

    if (!hasVideo && !hasImage) return "";

    if (hasVideo) {
      return settings.isYoutube && settings.youtubeEmbedUrl && settings.youtubeEmbedUrl.trim() !== ""
        ? this._buildYoutubeVideo(settings, config)
        : this._buildHostedVideo(settings, config);
    } else if (hasImage) {
      return this._buildBannerImage(settings, config);
    } else {
      return this._buildBannerPlaceholder(config);
    }
  }

  /**
   * Build YouTube video iframe
   * @private
   */
  _buildYoutubeVideo(settings, config) {
    const autoplayParam = settings.videoAutoplay ? "1" : "0";
    const loopParam = settings.videoLoop ? "1" : "0";
    const muteParam = settings.videoMuted ? "1" : "0";
    const controlsParam = settings.videoControls ? "1" : "0";

    return `
      <div class="video-container w-100 h-100 position-relative overflow-hidden rounded ${config.imgThumbnail}">
        <iframe
          class="position-absolute top-0 start-0"
          width="100%"
          height="100%"
          src="${settings.youtubeEmbedUrl}?autoplay=${autoplayParam}&loop=${loopParam}&mute=${muteParam}&controls=${controlsParam}"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          loading="lazy"
          style="opacity: ${settings.imgOpacity}; width: 100%; height: 100%; object-fit: cover; border: none; border-radius: inherit;">
        </iframe>
      </div>
    `;
  }

  /**
   * Build hosted video element
   * @private
   */
  _buildHostedVideo(settings, config) {
    const videoExtension = settings.videoUrl.split(".").pop().toLowerCase();
    const videoType = videoExtension === "webm" ? "video/webm" : videoExtension === "mov" ? "video/quicktime" : "video/mp4";

    return `
      <div class="video-container w-100 h-100 position-relative overflow-hidden rounded ${config.imgThumbnail}">
        <video
          class="banner-video"
          ${settings.videoAutoplay ? "autoplay" : ""}
          ${settings.videoLoop ? "loop" : ""}
          ${settings.videoMuted || settings.videoAutoplay ? "muted" : ""}
          ${settings.videoControls ? "controls" : ""}
          preload="none"
          poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="
          playsinline
          data-autoplay="${settings.videoAutoplay ? "true" : "false"}"
          style="opacity: ${settings.imgOpacity};">
          <source src="${settings.videoUrl}" type="${videoType}">
          Your browser does not support the video tag.
        </video>
      </div>
    `;
  }

  /**
   * Build banner image
   * @private
   */
  _buildBannerImage(settings, config) {
    return `
      <img
        class="img-fluid rounded ${config.imgThumbnail} w-100 h-100"
        src="${settings.imgSrc}"
        alt="${settings.imgAlt}"
        width="100%"
        height="100%"
        loading="lazy"
        fetchpriority="low"
        decoding="async"
        style="opacity: ${settings.imgOpacity}; object-fit: cover;">
    `;
  }

  /**
   * Build banner placeholder
   * @private
   */
  _buildBannerPlaceholder(config) {
    return `
      <div class="placeholder-image bg-light d-flex align-items-center justify-content-center rounded ${config.imgThumbnail} w-100 h-100" 
           style="min-height: 200px;">
        <span class="text-muted">Banner</span>
      </div>
    `;
  }

  /**
   * Build banner text overlay
   * @private
   */
  _buildBannerOverlay(settings) {
    if (!settings.title && !settings.description && !settings.btnText) {
      return "";
    }

    return `
      <div class="position-absolute d-flex flex-column justify-content-center align-items-center text-center" 
           style="top: 0; left: 0; right: 0; bottom: 0; z-index: 2; background: rgba(0,0,0,0.4); border-radius: inherit;">
        <div class="p-3">
          ${this._buildBannerTitle(settings)}
          ${this._buildBannerDescription(settings)}
          ${this._buildBannerButton(settings)}
        </div>
      </div>
    `;
  }

  /**
   * Build banner title
   * @private
   */
  _buildBannerTitle(settings) {
    if (!settings.title || settings.title === "") return "";

    return `
      <h3 class="title mb-2 fs-${settings.titleFontSize} text-white fw-bold">
        ${settings.title}
      </h3>
    `;
  }

  /**
   * Build banner description
   * @private
   */
  _buildBannerDescription(settings) {
    if (!settings.description || settings.description === "") return "";

    return `
      <div class="description mb-3 small text-white">
        ${settings.description}
      </div>
    `;
  }

  /**
   * Build banner button
   * @private
   */
  _buildBannerButton(settings) {
    if (!settings.btnText || settings.btnText === "") return "";

    return `
      <span class="btn btn-light btn-sm fw-bold">
        ${settings.btnText}
      </span>
    `;
  }

  /**
   * Get banner link wrapper
   * @private
   */
  _getBannerLinkWrapper(url) {
    const hasUrl = url && url !== "";
    return {
      start: hasUrl ? `<a href="${url}">` : "",
      end: hasUrl ? "</a>" : "",
    };
  }

  /**
   * Handle swatch color change
   * @private
   */
  _handleSwatchChange(e, swatch) {
    e.preventDefault();
    e.stopPropagation();

    const productItem = swatch.closest(".product-item");
    if (!productItem) return;

    const allSwatches = productItem.querySelectorAll(".color-swatches .btn");
    allSwatches.forEach((s) => s.classList.remove("selected"));
    swatch.classList.add("selected");

    // Update image
    const img = productItem.querySelector(".product-item-img--primary");
    if (img) {
      const rawSrc = swatch.getAttribute("data-img");

      if (rawSrc) {
        const width = swatch.getAttribute("data-img-width") || 600;
        const height = swatch.getAttribute("data-img-height") || 600;
        const baseUrl = rawSrc.split("?")[0];
        img.src = `${baseUrl}?width=${width}&height=${height}`;
        img.srcset = `${baseUrl}?width=${width}&height=${height} 1x, ${baseUrl}?width=${width * 2}&height=${height * 2} 2x`;
      }
    }

    // Update price display
    this._updateSwatchPrice(productItem, swatch);
  }

  /**
   * Update price display when swatch changes
   * @private
   * @param {Element} productItem - Product card container
   * @param {Element} swatch - Selected swatch button
   */
  _updateSwatchPrice(productItem, swatch) {
    const priceContainer = productItem.querySelector("[data-product-item-price]");
    if (!priceContainer) return;

    const price = parseFloat(swatch.getAttribute("data-price")) || 0;
    const compareAtPrice = parseFloat(swatch.getAttribute("data-compare-at-price")) || 0;

    if (price <= 0) return; // No valid price data

    const config = ConstructorUtils.getCachedThemeConfig();
    const formattedPrice = ConstructorUtils.formatMoney(price);
    const formattedComparePrice = ConstructorUtils.formatMoney(compareAtPrice);
    const isOnSale = compareAtPrice > price;
    const compValueLabel = window.theme?.product?.compValue || "Comp. value";

    if (isOnSale) {
      if (config.priceDisplayStyle === "strikethrough") {
        priceContainer.innerHTML = `
          <span class="product-item-price-compare text-muted me-1">
            <span class="visually-hidden">${config.productPriceRegular || "Regular price"}&nbsp;</span>
            <s>${formattedComparePrice}</s>
          </span>
          <span class="product-item-price-final">
            <span class="visually-hidden">${config.productPriceSale || "Sale price"}&nbsp;</span>
            ${formattedPrice}
          </span>
        `;
      } else {
        priceContainer.innerHTML = `
          <span class="product-item-price-final">${formattedPrice}</span>
          <span class="product-item-price-comp-value text-muted ms-1">${formattedComparePrice} ${compValueLabel}</span>
        `;
      }
    } else {
      priceContainer.innerHTML = `
        <span class="product-item-price-final">
          ${formattedPrice}
        </span>
      `;
    }
  }

  /**
   * Handle show all swatches button click
   * @private
   */
  _handleShowAllSwatches(e, btn) {
    e.preventDefault();
    e.stopPropagation();

    const swatchesList = btn.closest(".color-swatches");
    if (!swatchesList) return;

    swatchesList.querySelectorAll("li[hidden]").forEach((li) => {
      li.removeAttribute("hidden");
    });

    btn.remove();
  }

  /**
   * Handle load more button click
   * @private
   */
  _handleLoadMore(e, btn) {
    e.preventDefault();
    e.stopPropagation();

    const nextPage = parseInt(btn.dataset.nextPage);
    const collectionHandle = btn.dataset.collectionHandle;
    const perPage = parseInt(btn.dataset.perPage);

    this.hasLoadedMore = true;

    this.loadMoreProducts(collectionHandle, nextPage, perPage).then(() => {
      this._startInfiniteScroll(collectionHandle, perPage);
    });
  }

  _startInfiniteScroll(collectionHandle, perPage) {
    if (this._scrollHandler) return;

    this._scrollHandler = () => {
      if (this._isInfiniteLoading) return;

      const btn = document.querySelector(CONSTRUCTOR_SELECTORS.loadMoreBtn);
      if (!btn) return;

      const rect = btn.getBoundingClientRect();
      const nearBottom = rect.top <= window.innerHeight + 400;
      if (!nearBottom) return;

      const nextPage = parseInt(btn.dataset.nextPage);
      const perPageCurrent = parseInt(btn.dataset.perPage) || perPage;

      this._isInfiniteLoading = true;
      this.loadMoreProducts(collectionHandle, nextPage, perPageCurrent).then(() => {
        this._isInfiniteLoading = false;
      });
    };

    window.addEventListener('scroll', this._scrollHandler, { passive: true });

    // Trigger once immediately in case user is already at the bottom
    this._scrollHandler();
  }

  /**
   * Insert banners into products array at specified positions
   * @param {Array} products - Array of product HTML strings
   * @returns {Array} Modified array with banners inserted
   */
  insertBannersIntoProducts(products) {
    if (!products.length) return products;

    const activeBanners = this.getActiveBanners();
    if (!activeBanners.length) return products;

    const sortedBanners = activeBanners.slice().sort((a, b) => a.inject - b.inject);

    const result = [...products];
    let offset = 0;

    sortedBanners.forEach((banner) => {
      const position = banner.inject - 1 + offset;

      if (position >= 0 && position <= result.length) {
        const bannerHTML = this.renderCollectionBanner(banner);
        result.splice(position, 0, bannerHTML);
        offset++;
      }
    });

    return result;
  }

  bindEvents() {
    document.addEventListener("click", (e) => {
      const swatch = e.target.closest('[data-action="change-swatch"]');
      if (swatch) {
        this._handleSwatchChange(e, swatch);
        return;
      }

      const showAllBtn = e.target.closest('[data-action="show-all-swatches"]');
      if (showAllBtn) {
        this._handleShowAllSwatches(e, showAllBtn);
        return;
      }

      const loadMoreBtn = e.target.closest('[data-action="load-more"]');
      if (loadMoreBtn) {
        this._handleLoadMore(e, loadMoreBtn);
        return;
      }
    });

    window.addEventListener("popstate", () => {
      const urlParams = new URLSearchParams(window.location.search);

      const page = parseInt(urlParams.get("page")) || 1;
      const resultsPerPage = parseInt(urlParams.get("resultsPerPage")) || CONSTRUCTOR_CONFIG.RESULTS_PER_PAGE;

      let sortBy = urlParams.get("sortBy");
      let sortOrder = urlParams.get("sortOrder");

      const legacySort = urlParams.get("sort");
      if (!sortBy && legacySort && legacySort.includes(":")) {
        const [sb, so] = legacySort.split(":");
        sortBy = sb || "relevance";
        sortOrder = so || "descending";
      }
      sortBy = sortBy || "relevance";
      sortOrder = sortOrder || "descending";

      const urlFilters = urlParams.get("filters");
      let filters = {};
      if (urlFilters) {
        try {
          filters = JSON.parse(decodeURIComponent(urlFilters));
        } catch (e) {}
      }

      if (this.currentCollectionHandle) {
        this.handleBrowse(this.currentCollectionHandle, {
          page: page,
          resultsPerPage: resultsPerPage,
          sortBy: sortBy,
          sortOrder: sortOrder,
          filters: filters,
        });
      }
    });

    window.addEventListener(
      "resize",
      ConstructorUtils.debounce(() => {
        if (this.currentCollectionHandle && this.collectionBanners.length > 0) {
          const container = this.getResultsContainer();
          const grid = container?.querySelector("[data-cnstrc-browse]");

          if (grid) {
            const items = [...grid.children]
              .filter((li) => !li.classList.contains("collection-banner-item"))
              .map((li) => li.outerHTML);

            if (items.length > 0) {
              const withBanners = this.insertBannersIntoProducts(items).join("");
              const fragment = this._createProductFragment(withBanners);
              grid.replaceChildren(fragment);
              this._optimizeLCPImage(grid);
            }
          }
        }
      }, 300),
    );
  }

  getSmartAddToCartForm(product) {
    const variants = product.variants || [];

    const config = ConstructorUtils.getCachedThemeConfig();
    if (!config.showAtcForm) {
      return "";
    }

    if (this.productHasTag(product, "customized")) {
      return this.generateViewProductButton(product);
    }

    const availableVariants = variants.filter((v) => v.available !== false);

    if (availableVariants.length === 0) {
      return this.generateSoldOutButton();
    } else if (availableVariants.length === 1) {
      return this.generateSimpleAddToCartButton(product, availableVariants[0]);
    }

    return this.generateDropdownAddToCartButton(product, availableVariants);
  }

  getSmartAddToCartFormMobile(product) {
    const variants = product.variants || [];

    const config = ConstructorUtils.getCachedThemeConfig();
    if (!config.showAtcForm) {
      return "";
    }

    if (this.productHasTag(product, "customized")) {
      return this.generateViewProductButtonMobile(product);
    }

    const availableVariants = variants.filter((v) => v.available !== false);

    if (availableVariants.length === 0) {
      return this.generateSoldOutButtonMobile();
    } else if (availableVariants.length === 1) {
      return this.generateSimpleAddToCartButtonMobile(product, availableVariants[0]);
    }

    return this.generateDropdownAddToCartButtonMobile(product, availableVariants);
  }

  generateViewProductButton(product) {
    const config = ConstructorUtils.getCachedThemeConfig();
    const productUrl = product.url || product.constructorData?.url || `/products/${product.handle}`;

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <a href="${productUrl}" 
             class="view-product-btn-desktop"
             data-action="view-product"
             role="button">
            ${config.translations?.viewProduct || "View Product"}
          </a>
        </div>
      </div>
    `;
  }

  generateSoldOutButton() {
    const config = ConstructorUtils.getCachedThemeConfig();

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <button class="btn-atc btn w-100 btn-primary" data-action="sold-out" disabled>
            ${config.translations?.soldOut || "Sold Out"}
          </button>
        </div>
      </div>
    `;
  }

  generateSimpleAddToCartButton(product, variant) {
    const config = ConstructorUtils.getCachedThemeConfig();

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
                data-product-url="${product.url || `/products/${product.handle}`}">
            <input type="hidden" name="id" value="${variant.id}">
            <button class="btn-atc btn btn-md w-100 btn-primary"
                    type="submit"
                    data-action="add-to-cart-simple"
                    name="add"
                    data-cnstrc-btn="add_to_cart"
                    data-text-add-to-cart="${config.translations?.addToCart || "Add To Cart"}">
              ${config.translations?.addToCart || "Add To Cart"}
            </button>
          </form>
        </div>
      </div>
    `;
  }

  generateDropdownAddToCartButton(product, availableVariants) {
    const config = ConstructorUtils.getCachedThemeConfig();

    const dropdownItems = availableVariants
      .map((variant) => this.generateVariantDropdownItem(variant, config.formShowInventory, config.translations))
      .join("");

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
                data-product-url="${product.url || `/products/${product.handle}`}">
            <input type="hidden" name="id" value="${availableVariants[0].id}">
            <div class="w-200 dropup">
              <button type="button"
                      class="btn-atc btn btn-md dropdown-toggle btn-primary w-200"
                      data-action="add-to-cart-dropdown"
                      data-bs-toggle="dropdown"
                      data-text-add-to-cart="${config.translations?.addToCart || "Add To Cart"}"
                      aria-expanded="false"
                      aria-haspopup="true"
                      aria-label="${config.translations?.addToCart || "Add To Cart"} - Select variant">
                ${config.translations?.addToCart || "Add To Cart"}
              </button>
              <ul class="dropdown-menu shadow w-200">
                ${dropdownItems}
              </ul>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  generateVariantDropdownItem(variant, showInventory, translations) {
    const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

    return `
      <li class="${variant.inventory_policy || ""}">
        <button class="dropdown-item"
                onclick="onClickAtcFormVariant(this, event)"
                data-variant-id="${variant.id}"
                type="button"
                data-cnstrc-btn="add_to_cart"
                aria-label="Select ${ConstructorUtils.sanitizeHTML(variant.title)} variant"
                ${!variant.available ? "disabled aria-disabled='true'" : ""}>
          <div class="d-flex flex-column justify-content-start lh-sm">
            <span class="small">${ConstructorUtils.sanitizeHTML(variant.title)}</span>
            ${inventoryDisplay}
          </div>
        </button>
      </li>
    `;
  }

  getInventoryDisplay(variant, showInventory, translations) {
    if (!showInventory) {
      return "";
    }

    if (variant.available) {
      if (!variant.inventory_quantity || variant.inventory_quantity <= 0 || variant.inventory_quantity >= 10) {
        return `<small class="text-success d-block">${translations?.inStock || "In stock"}</small>`;
      } else {
        const stockText = translations?.stockCount || "Only [count] left in stock";
        return `<small class="text-danger d-block">${stockText.replace("[count]", variant.inventory_quantity)}</small>`;
      }
    } else {
      return `<small class="text-muted d-block">${translations?.soldOut || "Sold out"}</small>`;
    }
  }

  generateViewProductButtonMobile(product) {
    const config = ConstructorUtils.getCachedThemeConfig();
    const productUrl = product.url || product.constructorData?.url || `/products/${product.handle}`;

    return `
      <div class="mobile-view d-block d-lg-none">
        <a href="${productUrl}" 
           class="btn-vp w-100 d-block"
           data-action="view-product-mobile"
           role="button">
          ${config.translations?.viewProduct || "View Product"}
        </a>
      </div>
    `;
  }

  generateSoldOutButtonMobile() {
    const config = ConstructorUtils.getCachedThemeConfig();

    return `
      <div class="mobile-view d-block d-lg-none">
        <button class="btn-atc btn w-100 btn-primary" disabled>
          ${config.translations?.soldOut || "Sold Out"}
        </button>
      </div>
    `;
  }

  generateSimpleAddToCartButtonMobile(product, variant) {
    const config = ConstructorUtils.getCachedThemeConfig();

    return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
              data-product-url="${product.url}">
          <input type="hidden" name="id" value="${variant.id}">
          <button class="btn-atc btn btn-md w-100 btn-primary"
                  type="submit"
                  name="add"
                  data-cnstrc-btn="add_to_cart"
                  data-text-add-to-cart="${config.translations?.addToCart || "Add to cart"}">
            ${config.translations?.addToCart || "Add to cart"}
          </button>
        </form>
      </div>
    `;
  }

  generateDropdownAddToCartButtonMobile(product, availableVariants) {
    const config = ConstructorUtils.getCachedThemeConfig();

    const variantItems = availableVariants
      .map((variant) => this.generateVariantDropdownItemMobile(variant, config.formShowInventory, config.translations))
      .join("");

    return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${ConstructorUtils.sanitizeHTML(product.title)}"
              data-product-url="${product.url}">
          <input type="hidden" name="id" value="${availableVariants[0].id}">
          <div class="w-100 dropup">
            <button type="button"
                    class="btn-atc w-100"
                    data-action="add-to-cart-mobile"
                    data-bs-toggle="dropdown"
                    data-text-add-to-cart="${config.translations?.addToCart || "Add to cart"}"
                    aria-expanded="false"
                    aria-haspopup="true"
                    aria-label="${config.translations?.addToCart || "Add to cart"} - Select variant">
              ${config.translations?.addToCart || "Add to cart"}
            </button>
            <ul class="dropdown-menu shadow w-100">
              ${variantItems}
            </ul>
          </div>
        </form>
      </div>
    `;
  }

  generateVariantDropdownItemMobile(variant, showInventory, translations) {
    const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

    return `
      <li class="${variant.inventory_policy || ""}">
        <button class="dropdown-item"
                onclick="onClickAtcFormVariant(this, event)"
                data-variant-id="${variant.id}"
                type="button"
                data-cnstrc-btn="add_to_cart"
                aria-label="Select ${ConstructorUtils.sanitizeHTML(variant.title)} variant"
                ${!variant.available ? "disabled aria-disabled='true'" : ""}>
          <div class="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center lh-sm">
            <span class="small">${ConstructorUtils.sanitizeHTML(variant.title)}</span>
            ${inventoryDisplay}
          </div>
        </button>
      </li>
    `;
  }
}

(function initConstructorBrowse() {
  if (!window.ConstructorUtils || typeof ConstructorioClient === "undefined" || !window.constructorio) {
    requestAnimationFrame(() => setTimeout(initConstructorBrowse, 50));
    return;
  }

  if (!window.constructorBrowse) {
    const shouldInitialize = document.getElementById("collection") || document.querySelector("[data-cnstrc-browse]");

    if (shouldInitialize) {
      window.constructorBrowse = new ConstructorBrowse();
      window.constructorBrowse.checkUrlAndBrowse();
    }
  }
})();

